import { AnyTranslationsFn } from "@/types/locale";
import { Page, PathMatchesRecord } from "@/types/pages";
import { MenuItem } from "@/types/ui";
import { UseRouteInfo, useRouteInfo } from "@hooks/pages/use-route-info";
import { Icon } from "@tabler/icons-react";
import { LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { useAnyTranslations } from "@lib/i18n/utils";

/**
 * Generates a sidebar menu item object based on the provided parameters.
 *
 * @param {Object} params - The parameters for generating the menu item.
 * @param {AnyTranslationsFn} params.localeAs - A function to fetch localized strings for the menu item title.
 * @param {Page} params.page - The page object containing localization keys and path information.
 * @param {LucideIcon} params.icon - The icon representing the menu item.
 * @param {boolean} [params.isDisabled] - Whether the menu item should be disabled.
 * @param {UseRouteInfo} params.pageInfo - Information about the current route.
 * @param {PathMatchesRecord} [params.matches] - Path matches for dynamic routes, if applicable.
 * @param {MenuItem[]} [params.subItems] - A list of submenu items for the menu item.
 * @param {MenuItem[]} [params.burgerItems] - A list of burger menu items, if applicable.
 * @returns {MenuItem} An object representing the constructed sidebar menu item.
 */
export const buildSidebarMenuItem = ({
  localeAs,
  page,
  icon,
  isDisabled,
  pageInfo,
  matches,
  subItems,
  burgerItems,
  isFullButton,
}: {
  localeAs: AnyTranslationsFn;
  page: Page;
  icon?: LucideIcon | Icon;
  isDisabled?: boolean;
  pageInfo?: UseRouteInfo;
  matches?: PathMatchesRecord;
  subItems?: MenuItem[];
  burgerItems?: MenuItem[];
  isFullButton?: boolean;
}): MenuItem => ({
  label: localeAs(page.localizationKeys.title),
  description: localeAs(page.localizationKeys.description),
  url: isDisabled ? "#" : page.path(matches),
  icon: icon ?? page?.icon,
  isDisabled,
  isActive: pageInfo?.page?.pathTemplate === page.pathTemplate,
  items: subItems,
  burgerItems,
  isFullButton,
});

/**
 * A custom hook that generates an array of sidebar menu items for a feature based on provided inputs.
 *
 * @param {function(localeAs: AnyTranslationsFn, pageInfo: UseRouteInfo): MenuItem[]} buildSidebarMenuItemsFn -
 * A function that constructs the sidebar menu items. It takes a translation function and a route information
 * object as parameters and returns an array of `MenuItem`.
 *
 * @returns {MenuItem[]} The generated array of `MenuItem` objects to be used in the feature's sidebar menu.
 */
export const useFeatureMenu = (
  buildSidebarMenuItemsFn: (localeAs: AnyTranslationsFn, pageInfo: UseRouteInfo) => MenuItem[],
): MenuItem[] => {
  const tAny = useAnyTranslations();
  const pageInfo = useRouteInfo();

  return useMemo(
    () => buildSidebarMenuItemsFn?.(tAny, pageInfo),
    [buildSidebarMenuItemsFn, pageInfo, tAny],
  );
};
